import { isArray, isBoolean, isNumber, isString } from 'lodash';
import { baseInputValidationScheme as baseScheme } from '../constants';
import { TContext, TTag } from './types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import UniversalChildren from '../../UniversalTag/UniversalChildren';
import { getResizedImage } from './helpers/getResizedImage';
import { TInputState } from '../CustomForm/types';
import { TBaseTagContext } from '../types';
import { validate } from '@krugom/validator';

const CustomInputImage: React.FC<{
  tagProps: TTag;
  context: Partial<TBaseTagContext>;
}> = React.memo(({ tagProps, context }) => {
  const [src, setSrc] = useState<TContext['src']>();
  const validationScheme = useMemo(
    () => ({
      ...baseScheme,
      properties: {
        ...baseScheme.properties,
        initialValue: {
          type: 'custom',
          validator: (v: any) => isBoolean(v) || isNumber(v) || isString(v),
        },
      },
    }),
    [baseScheme]
  );

  const result = useMemo(() => validate(tagProps, validationScheme), [tagProps, validationScheme]);

  const {
    name,
    children,
    required,
    crop,
    initialValue,
    size,
    quality,
    type,
    formOnly,
    label,
    description,
    placeholder,
    validation,
  } = tagProps;
  const formContext = context.form;
  const formContextInput = formContext?.inputs[name];
  const [showHelp, setShowHelp] = useState<boolean>(false);

  const initialState: TInputState = useMemo(
    () => ({
      name: name,
      label,
      description,
      placeholder,
      type: 'image',
      preview: initialValue,
      errors: [],
      touched: false,
      required,
      initialValue,
      sizeX: size.width,
      sizeY: size.height,
      formOnly,
      reset: false,
      validation,
      value: initialValue,
    }),
    [formOnly, initialValue, name, required, size.height, size.width]
  );

  useEffect(() => {
    if (context.form) {
      if (!formContextInput) {
        context.form.handleSetInput(name, initialState);
      } else {
        if (formContextInput.reset) {
          context.form.handleSetInput(name, initialState);
          setSrc(undefined);
        }
      }
    }
  }, [context.form, formContextInput, initialState, name]);

  const handleCleanup = useCallback(() => {
    setSrc(undefined);
    context.form?.handleSetInput(name, { ...initialState, value: null, preview: null });
  }, [context.form, initialState, name]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!formContextInput) return;
      const { files } = event.target;
      if (!files || isArray(files)) return;
      setSrc(undefined);
      const newFile = files[0];

      new Promise(res => {
        const resized = getResizedImage(newFile, size.width, size.height, type, quality);
        res(resized);
      }).then(res => {
        const resizedImageData = res as {
          scaledWidth: number;
          scaledHeight: number;
          resizedData: Blob;
          resizedPreview: string;
        };

        context.form?.handleSetInput(name, {
          ...formContextInput,
          value: resizedImageData.resizedData,
          preview: resizedImageData.resizedPreview,
          sizeX: resizedImageData.scaledWidth,
          sizeY: resizedImageData.scaledHeight,
          fileName: newFile.name,
          mimeType: type,
          touched: true,
          errors: [],
        });
      });
    },
    [context.form, formContextInput, name, size.height, size.width, type]
  );

  const handleShowHelp = useCallback(() => setShowHelp(showHelp), [showHelp]);

  const inputContext: TContext = useMemo(
    () => ({
      name,
      label,
      description,
      placeholder,
      type: initialState.type,
      required,
      initialValue,
      errors: formContextInput?.errors,
      touched: formContextInput?.touched,
      sizeX: size.width,
      sizeY: size.height,
      value: formContextInput?.value,
      preview: formContextInput?.preview,
      handleChange,
      handleCleanup,
      handleShowHelp,
      src,
      showHelp,
    }),
    [
      formContextInput?.errors,
      formContextInput?.touched,
      formContextInput?.value,
      handleChange,
      handleCleanup,
      handleShowHelp,
      initialState.type,
      initialValue,
      name,
      required,
      size.height,
      size.width,
      src,
      showHelp,
      label,
      description,
      placeholder,
    ]
  );

  const newContexts: Partial<TBaseTagContext> = useMemo(
    () => ({
      ...context,
      input: inputContext,
    }),
    [context, inputContext]
  );

  if (!formContext) {
    console.warn('Tag <InputSimpleContext> must be child of the <FormContext> tag!');
    return null;
  }

  if (!result.isValid) {
    console.error(
      'Тэг: $InputImageContext;',
      `Путь: ${context.path?.join('.') || 'Не указан'};`,
      result.errors
    );
    return <UniversalChildren context={context}>{tagProps.children}</UniversalChildren>;
  }

  return <UniversalChildren context={newContexts}>{children}</UniversalChildren>;
});
CustomInputImage.displayName = 'CustomInputImage';
export default CustomInputImage;
